import { MicVAD } from '@ricky0123/vad-web';

import { postVoiceEvent } from '../../api';

/** Target playback volume while a barge-in candidate is being evaluated. */
const DUCK_VOLUME = 0.5;
/** Full playback volume after confirm or misfire restore. */
const FULL_VOLUME = 1;
/** Ramp duration for duck / restore to avoid audible clicks. */
const VOLUME_FADE_MS = 80;
/**
 * Minimum sustained speech before we treat onset as a real barge-in (not
 * noise). This is the "at least ~2 intelligible words" gate: Silero must see
 * this many ms of speech-probability frames before `onSpeechRealStart` fires
 * and we send `response.cancel`. Below it, the onset is treated as a misfire.
 *
 * IMPORTANT: the default vad-web model is `legacy` (frameSamples 1536 →
 * 96ms/frame), so this value floors to `Math.floor(MIN_SPEECH_MS / 96)`
 * frames. 280ms → only 2 frames (~190ms) → confirmed real barge-in on a brief
 * cough/clatter in ~90ms, which then cancels the live response. 600ms → 6
 * frames (~576ms) of sustained speech, which brief noise won't reach — so the
 * server's `interrupt_response: false` blip-and-resume plays through and the
 * assistant keeps talking. Tunable via real-call `vad.*` instrumentation.
 */
const MIN_SPEECH_MS = 600;
/**
 * Silence (ms) after speech before Silero ends/aborts a segment. vad-web's
 * default is 1400ms — far too long here: a brief noise that doesn't reach
 * `MIN_SPEECH_MS` would keep playback ducked for >1.3s before `onVADMisfire`
 * restores it. 600ms restores volume promptly after the noise passes while
 * still bridging natural intra-utterance gaps during a real barge-in.
 */
const REDEMPTION_MS = 600;

const VAD_WEB_VERSION = '0.0.30';
const ONNX_RUNTIME_WEB_VERSION = '1.22.0';
const CDN_BASE_ASSET_PATH = `https://cdn.jsdelivr.net/npm/@ricky0123/vad-web@${VAD_WEB_VERSION}/dist/`;
const CDN_ONNX_WASM_BASE_PATH = `https://cdn.jsdelivr.net/npm/onnxruntime-web@${ONNX_RUNTIME_WEB_VERSION}/dist/`;

export interface VoiceBargeInContext {
  conversationId: string;
  getStream: () => MediaStream | undefined;
  getAudioEl: () => HTMLAudioElement | undefined;
  getDataChannel: () => RTCDataChannel | undefined;
  isAssistantAudible: () => boolean;
  isResponseInFlight: () => boolean;
  getAuthHeaders: () => Record<string, string>;
  sendDataChannel: (event: unknown) => void;
  /** Called when Silero fails to init — caller should restore server-side barge-in. */
  onInitFailed: () => void;
  /**
   * Fired the moment the client VAD CONFIRMS real, sustained user speech
   * (`onSpeechRealStart`), regardless of whether the assistant is audible.
   * The session hook uses this as a "the user is definitely talking" signal
   * to arm the liveness watchdog: if OpenAI emits no matching
   * `input_audio_buffer.speech_started` shortly after, the Realtime session
   * is dead even though the WebRTC transport is still up.
   */
  onConfirmedUserSpeech?: () => void;
  /**
   * Optional console logger so Silero VAD activity (duck / confirmed barge-in /
   * misfire) shows up in the same `logVoiceChat` browser stream as the OpenAI /
   * data-channel events. No-op in production unless the flag is set.
   */
  log?: (category: string, payload?: Record<string, unknown>) => void;
}

/**
 * Client-side Silero VAD gate for assistant barge-in. With
 * `interrupt_response: false` on the Realtime session, OpenAI no longer
 * auto-cuts on noise; this controller ducks playback on suspected onset,
 * confirms real speech via `onSpeechRealStart`, and only then sends
 * `response.cancel`.
 */
export class VoiceBargeInController {
  private vad: MicVAD | null = null;
  private deps: VoiceBargeInContext | null = null;
  private isDucked = false;
  private duckStartedAt: number | null = null;
  private fadeRafId: number | null = null;
  private started = false;

  public async start(ctx: VoiceBargeInContext): Promise<boolean> {
    if (this.started) return true;
    this.deps = ctx;

    const stream = ctx.getStream();
    if (!stream) {
      this.emitVadEvent('vad.init_failed', { reason: 'no_stream' });
      ctx.onInitFailed();
      return false;
    }

    try {
      this.vad = await MicVAD.new({
        baseAssetPath: CDN_BASE_ASSET_PATH,
        onnxWASMBasePath: CDN_ONNX_WASM_BASE_PATH,
        startOnLoad: false,
        positiveSpeechThreshold: 0.55,
        negativeSpeechThreshold: 0.35,
        minSpeechMs: MIN_SPEECH_MS,
        redemptionMs: REDEMPTION_MS,
        getStream: async () => {
          const s = this.deps?.getStream();
          if (!s) {
            throw new Error('Voice barge-in mic stream unavailable');
          }
          return s;
        },
        // Shared mic stream — never stop tracks on VAD pause/destroy.
        pauseStream: async () => {},
        resumeStream: async () => {
          const s = this.deps?.getStream();
          if (!s) {
            throw new Error('Voice barge-in mic stream unavailable');
          }
          return s;
        },
        onSpeechStart: () => {
          void this.handleSpeechStart();
        },
        onSpeechRealStart: () => {
          void this.handleSpeechRealStart();
        },
        onVADMisfire: () => {
          void this.handleVadMisfire();
        },
        onSpeechEnd: () => {},
        onFrameProcessed: () => {},
      });
      await this.vad.start();
      this.started = true;
      return true;
    } catch (err) {
      this.emitVadEvent('vad.init_failed', {
        reason: err instanceof Error ? err.message : String(err),
      });
      ctx.onInitFailed();
      this.vad = null;
      return false;
    }
  }

  public async stop(): Promise<void> {
    this.cancelVolumeFade();
    this.restoreVolumeImmediate();
    this.isDucked = false;
    this.duckStartedAt = null;

    if (this.vad) {
      try {
        await this.vad.pause();
        await this.vad.destroy();
      } catch {
        // ignore teardown errors
      }
      this.vad = null;
    }

    this.deps = null;
    this.started = false;
  }

  private shouldGateBargeIn(): boolean {
    const deps = this.deps;
    if (!deps) return false;
    return deps.isAssistantAudible() || deps.isResponseInFlight();
  }

  private async handleSpeechStart(): Promise<void> {
    if (!this.shouldGateBargeIn()) return;

    this.duckStartedAt = Date.now();
    this.fadeVolumeTo(DUCK_VOLUME);
    this.isDucked = true;
    this.emitVadEvent('vad.ducked', {
      duckVolume: DUCK_VOLUME,
      assistantAudible: this.deps?.isAssistantAudible() ?? false,
      responseInFlight: this.deps?.isResponseInFlight() ?? false,
    });
  }

  private async handleSpeechRealStart(): Promise<void> {
    // Liveness signal FIRST — before the barge-in gate. The user is genuinely
    // speaking now (sustained ≥ MIN_SPEECH_MS); fire this even when the
    // assistant is silent / no response is in flight (exactly the dead-session
    // case the watchdog must catch), where `shouldGateBargeIn()` returns false
    // and the rest of this method early-returns.
    this.deps?.onConfirmedUserSpeech?.();

    if (!this.shouldGateBargeIn()) {
      if (this.isDucked) {
        this.restoreVolumeImmediate();
        this.isDucked = false;
        this.duckStartedAt = null;
      }
      return;
    }

    const duckDurationMs =
      this.duckStartedAt != null ? Math.max(0, Date.now() - this.duckStartedAt) : undefined;

    this.restoreVolumeImmediate();
    this.isDucked = false;
    this.duckStartedAt = null;

    const deps = this.deps;
    if (!deps) return;

    // Only cancel when a response is genuinely still generating. If the
    // response already finished and only the audio tail is draining,
    // `response.cancel` hits nothing and OpenAI replies `response_cancel_not_active`
    // (a harmless but noisy error). `response.cancel` accepts ONLY `type`
    // (+ optional `event_id`) — any extra field is rejected with
    // `unknown_parameter`, so the payload stays bare. The human-readable reason
    // lives in the `vad.confirmed_interrupt` instrumentation event below.
    const responseInFlight = deps.isResponseInFlight();
    if (responseInFlight) {
      deps.sendDataChannel({ type: 'response.cancel' });
    }

    this.emitVadEvent('vad.confirmed_interrupt', {
      duckDurationMs,
      assistantAudible: deps.isAssistantAudible(),
      responseInFlight,
      cancelSent: responseInFlight,
    });
  }

  private async handleVadMisfire(): Promise<void> {
    if (!this.isDucked) return;

    const duckDurationMs =
      this.duckStartedAt != null ? Math.max(0, Date.now() - this.duckStartedAt) : undefined;

    this.restoreVolumeImmediate();
    this.isDucked = false;
    this.duckStartedAt = null;

    this.emitVadEvent('vad.misfire', {
      duckDurationMs,
      minSpeechMs: MIN_SPEECH_MS,
    });
  }

  private fadeVolumeTo(target: number): void {
    const audioEl = this.deps?.getAudioEl();
    if (!audioEl) return;

    this.cancelVolumeFade();
    const startVolume = audioEl.volume;
    const startTime = performance.now();

    const step = () => {
      const elapsed = performance.now() - startTime;
      const t = Math.min(1, elapsed / VOLUME_FADE_MS);
      audioEl.volume = startVolume + (target - startVolume) * t;
      if (t < 1) {
        this.fadeRafId = requestAnimationFrame(step);
      } else {
        this.fadeRafId = null;
        audioEl.volume = target;
      }
    };

    this.fadeRafId = requestAnimationFrame(step);
  }

  private restoreVolumeImmediate(): void {
    this.cancelVolumeFade();
    const audioEl = this.deps?.getAudioEl();
    if (audioEl) {
      audioEl.volume = FULL_VOLUME;
    }
  }

  private cancelVolumeFade(): void {
    if (this.fadeRafId != null) {
      cancelAnimationFrame(this.fadeRafId);
      this.fadeRafId = null;
    }
  }

  private emitVadEvent(type: string, payload: Record<string, unknown>): void {
    const deps = this.deps;
    if (!deps) return;
    // Surface to the browser console (same stream as OpenAI/data-channel logs)
    // so duck / confirmed barge-in / misfire are visible while debugging.
    deps.log?.(type, payload);
    void postVoiceEvent({
      conversationId: deps.conversationId,
      events: [{ type, t: Date.now(), ...payload }],
      headersToInject: deps.getAuthHeaders(),
    });
  }
}

/** Session patch to restore OpenAI server-side barge-in when client VAD fails. */
export function buildServerBargeInFallbackSessionUpdate(): Record<string, unknown> {
  return {
    type: 'session.update',
    session: {
      type: 'realtime',
      audio: {
        input: {
          turn_detection: {
            type: 'semantic_vad',
            eagerness: 'auto',
            interrupt_response: true,
            create_response: true,
          },
        },
      },
    },
  };
}
